import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAdmin } from "@/lib/adminContext";
import { LayoutDashboard, ListChecks, LogOut, Loader2, ShieldCheck, Building, Users } from "lucide-react";
import EnableNotificationsBanner from "@/components/EnableNotificationsBanner";

const IT_DEPARTMENT = "IT / Information Technology";

export default function AdminLayout() {
  const navigate = useNavigate();
  const { admin, loading, signOut } = useAdmin();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[color:var(--enka-blue)]" />
      </div>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;

  const logout = () => {
    signOut();
    navigate("/admin/login");
  };

  const linkBase =
    "flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors";
  const linkActive = "bg-white/15 text-white";
  const linkIdle = "text-white/80 hover:bg-white/10 hover:text-white";

  const isSuper = admin.role === "super";
  const canManageUsers = isSuper || (admin.scope || []).includes(IT_DEPARTMENT);

  return (
    <div className="min-h-screen flex bg-[#f6f8fc]" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 shrink-0 flex-col bg-[color:var(--enka-blue)] text-white">
        <div className="px-5 py-5 flex items-center gap-3 border-b border-white/10">
          <div className="leading-tight">
            <div className="font-display text-base" data-testid="admin-brand-title">IQ3B9</div>
            <div className="text-[11px] text-white/70">Admin Panel</div>
          </div>
        </div>

        {/* Identity / scope panel */}
        <div className="px-4 py-4 border-b border-white/10" data-testid="admin-identity-panel">
          <div className="flex items-center gap-2 mb-2">
            {isSuper ? (
              <ShieldCheck className="h-4 w-4 text-amber-300" />
            ) : (
              <Building className="h-4 w-4 text-blue-200" />
            )}
            <div className="text-sm font-semibold truncate" data-testid="admin-display-name">
              {admin.display}
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-white/60 mb-1">
            Logged in as <span className="text-white/80">{admin.username}</span>
          </div>
          {isSuper ? (
            <div
              className="inline-flex items-center gap-1 text-[11px] bg-amber-400/15 text-amber-200 px-1.5 py-0.5 rounded"
              data-testid="admin-scope-super"
            >
              <ShieldCheck className="h-3 w-3" />
              Full access (all departments)
            </div>
          ) : (
            <div className="text-[11px] text-white/75" data-testid="admin-scope-list">
              <div className="text-white/55 mb-1">Scope:</div>
              <ul className="space-y-0.5">
                {admin.scope.map((d) => (
                  <li
                    key={d}
                    className="truncate before:content-['•'] before:mr-1.5 before:text-white/40"
                    title={d}
                    data-testid={`admin-scope-item-${d}`}
                  >
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <nav className="p-3 space-y-1 flex-1">
          <NavLink
            to="/admin"
            end
            className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
            data-testid="nav-dashboard"
          >
            <LayoutDashboard className="h-4 w-4" /> Dashboard
          </NavLink>
          <NavLink
            to="/admin/tickets"
            className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
            data-testid="nav-tickets"
          >
            <ListChecks className="h-4 w-4" /> Tickets
          </NavLink>
          {canManageUsers && (
            <NavLink
              to="/admin/users"
              className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkIdle}`}
              data-testid="nav-users"
            >
              <Users className="h-4 w-4" /> Users
            </NavLink>
          )}
        </nav>
        <div className="p-3 border-t border-white/10">
          <button
            type="button"
            onClick={logout}
            className={`${linkBase} ${linkIdle} w-full`}
            data-testid="admin-logout-btn"
          >
            <LogOut className="h-4 w-4" /> Logout
          </button>
        </div>
      </aside>

      {/* Mobile top-bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 enka-header-bar flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="leading-tight min-w-0">
            <div className="font-display truncate">IQ3B9 Admin Panel</div>
            <div className="text-[10px] text-white/70 truncate" data-testid="admin-display-name-mobile">
              {admin.display}
            </div>
          </div>
        </div>
        <button onClick={logout} className="text-sm text-white/90" data-testid="admin-logout-mobile">
          Logout
        </button>
      </div>

      <main className="flex-1 min-w-0 md:ml-0 pt-16 md:pt-0">
        <EnableNotificationsBanner />
        <Outlet />
      </main>
    </div>
  );
}
